import { sortBy, each, isEmpty } from 'lodash'

import { createUID } from 'common/utils/createUID'
import { getNewEmptyWeek } from '../programModels'
import { createPastedMultiWkts, createPastedWorkouts, updateExistingWktsOrder } from './pasteWorkoutUtils'
import { updateManyWorkoutContentTags } from './contentTagUtils'
import { createPartWithNewIds } from '../handlers/commonPartHandlers'

export const handlePasteWeek = async ({
  copiedWeekWktIds,
  pastedAtWeekWktIds,
  reorderProgram,
  updateWorkouts,
  updateMultiWktDays,
  getWorkoutsByIds,
  programId,
  orgId,
  createAlert,
  wktIdsSorted,
  multiWktDays,
}) => {
  const { data: copiedWeekWkts } = await getWorkoutsByIds({ orgId, wktIds: copiedWeekWktIds })
  const { data: pastedAtWeekWkts } = await getWorkoutsByIds({ orgId, wktIds: pastedAtWeekWktIds })

  let copiedMultiWktDayIds = []
  each(copiedWeekWkts, (wkt) => {
    if (wkt?.multiWktDayId) {
      copiedMultiWktDayIds.push(wkt.multiWktDayId)
    }
  })

  let copiedMultiWktIds = []
  copiedMultiWktDayIds.forEach((dayId) => {
    if (multiWktDays?.[dayId]?.workouts) {
      const wktIds = multiWktDays?.[dayId]?.workouts || []
      copiedMultiWktIds = [...copiedMultiWktIds, ...wktIds]
    }
  })

  const { data: copiedMultiWkts } = await getWorkoutsByIds({ orgId, wktIds: copiedMultiWktIds })

  const sortedCopiedWeekWkts = sortBy(Object.values(copiedWeekWkts), 'dayIdx')
  const sortedPastedAtWeekWkts = sortBy(Object.values(pastedAtWeekWkts), 'dayIdx')

  const isPastedAtWeekEmpty = sortedPastedAtWeekWkts.every((wkt) => wkt.type === 'empty' && !wkt?.multiWktDayId)

  if (isPastedAtWeekEmpty) {
    let updatedEmptyWeekWkts = {}
    let updatedProgramMultiWktDays = {}

    each(sortedPastedAtWeekWkts, (wkt, idx) => {
      const copiedWkt = sortedCopiedWeekWkts[idx]
      const partsWithNewIds = copiedWkt.parts.map((part) => createPartWithNewIds(part))
      updatedEmptyWeekWkts[wkt.id] = {
        ...copiedWkt,
        id: wkt.id,
        dayIdx: wkt.dayIdx,
        parts: partsWithNewIds,
        multiWktDayId: copiedWkt?.multiWktDayId ? createUID() : null,
      }

      // if multiWkt, copy/create new workouts of this multiWkt
      if (copiedWkt?.multiWktDayId) {
        const { multiWktsUpdate, programMultiWktDaysUpdate } = createPastedMultiWkts({
          copiedWkt,
          copiedMultiWkts,
          multiWktDays,
          pastedWkt: updatedEmptyWeekWkts[wkt.id],
        })
        updatedEmptyWeekWkts = { ...updatedEmptyWeekWkts, ...multiWktsUpdate }
        updatedProgramMultiWktDays = { ...updatedProgramMultiWktDays, ...programMultiWktDaysUpdate }
      }
    })

    if (!isEmpty(updatedProgramMultiWktDays)) {
      await updateMultiWktDays({ orgId, programId, update: updatedProgramMultiWktDays })
    }
    await updateWorkouts({ orgId, workouts: updatedEmptyWeekWkts })
    await updateManyWorkoutContentTags({ orgId, workouts: updatedEmptyWeekWkts, programId, updateType: 'add' })
  } else {
    const pastedAtDayIdx = sortedPastedAtWeekWkts[0].dayIdx

    const {
      progWkts: progWktsPasted,
      wkts: newPastedWkts,
      wktsOverwritten,
      multiWkts,
      progMultiWktDays,
    } = createPastedWorkouts({
      copiedWorkouts: sortedCopiedWeekWkts,
      shouldOverwriteByCopyIdx: false,
      wktIdsSorted,
      pastedAtDayIdx,
      multiWktDays,
      copiedMultiWkts,
    })

    const {
      reorderedProgWkts: progWktsForExistingWkts,
      firebaseWktsUpdate: existingWktsUpdates,
      progMultiWktDaysUpdate,
    } = updateExistingWktsOrder({
      wktIdsSorted,
      pastedAtDayIdx,
      numNewWkts: 7,
      wktsOverwritten,
      emptyWktsToRemove: {},
      multiWktDays,
    })

    const reorderedProgWkts = {
      ...progWktsPasted,
      ...progWktsForExistingWkts,
    }

    const workoutsUpdate = {
      ...newPastedWkts,
      ...existingWktsUpdates,
      ...multiWkts,
    }

    if (!isEmpty(progMultiWktDays) || !isEmpty(progMultiWktDaysUpdate)) {
      await updateMultiWktDays({ orgId, programId, update: { ...progMultiWktDays, ...progMultiWktDaysUpdate } })
    }

    await reorderProgram({
      orgId,
      programId,
      reorderedProgramWkts: reorderedProgWkts,
      fbUpdateWkts: workoutsUpdate,
    })
    await updateManyWorkoutContentTags({
      orgId,
      workouts: { ...newPastedWkts, ...multiWkts },
      programId,
      updateType: 'add',
    })
  }

  createAlert({ text: 'Week pasted!', type: 'success' })
}

export const handleInsertNewWeek = async ({
  weekIdx,
  reorderProgram,
  programId,
  orgId,
  wktIdsSorted,
  multiWktDays,
  updateMultiWktDays,
  createAlert,
}) => {
  const { newWorkouts: newWeekWorkouts, idToDayIdx: newProgWeekWkts } = getNewEmptyWeek(weekIdx)

  const progWktsUpdate = {}
  const workoutsUpdate = {}
  const progMultiWktDaysUpdate = {}

  let wktIdsToDayIds = {}
  each(multiWktDays || {}, (day) => {
    const workoutIds = day?.workouts || []
    workoutIds.forEach((id) => {
      wktIdsToDayIds[id] = day?.id
    })
  })

  each(wktIdsSorted, (wktId, dayIdx) => {
    const currWeekAndAfterRange = dayIdx + 1 >= (weekIdx + 1) * 7 - 6
    if (currWeekAndAfterRange) {
      const updatedDayIdx = dayIdx + 7

      progWktsUpdate[wktId] = updatedDayIdx

      const fbKey = `${wktId}/dayIdx`
      workoutsUpdate[fbKey] = updatedDayIdx

      // multiWkts logic
      if (wktIdsToDayIds[wktId]) {
        const dayId = wktIdsToDayIds[wktId]
        progMultiWktDaysUpdate[dayId] = { ...multiWktDays[dayId], dayIdx: updatedDayIdx }
        const wktIds = multiWktDays?.[dayId]?.workouts || []
        wktIds.forEach((id) => {
          if (id !== wktId) {
            workoutsUpdate[`${id}/dayIdx`] = updatedDayIdx
          }
        })
      }
    } else {
      progWktsUpdate[wktId] = dayIdx
    }
  })

  const reorderedProgramWkts = {
    ...newProgWeekWkts,
    ...progWktsUpdate,
  }

  const fbUpdateWkts = {
    ...newWeekWorkouts,
    ...workoutsUpdate,
  }

  if (!isEmpty(progMultiWktDaysUpdate)) {
    await updateMultiWktDays({ orgId, programId, update: progMultiWktDaysUpdate })
  }

  await reorderProgram({
    orgId,
    programId,
    reorderedProgramWkts,
    fbUpdateWkts,
  })

  createAlert({ text: 'Week inserted!', type: 'success' })
}
